import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import {
  AlertOctagon,
  AlertTriangle,
  Bug,
  Search,
  RefreshCw,
  Calendar,
  Clock,
  MessageSquare,
  FileCode,
  Copy,
  Check,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  X,
  Code,
  Terminal,
  Filter,
  Eye,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "../../lib/utils";

// Helper to format timestamps
function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (_) {
    return dateStr;
  }
}

// Pretty-print JSON if string contains JSON error payload
function formatErrorMessage(msg) {
  if (!msg) return "";
  try {
    // Check if error contains embedded JSON
    const jsonMatch = msg.match(/(\{[\s\S]*\})/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[1]);
      const prefix = msg.slice(0, jsonMatch.index).trim();
      return {
        prefix,
        json: JSON.stringify(parsed, null, 2),
        isJson: true,
      };
    }
  } catch (_) {}
  return { prefix: msg, json: null, isJson: false };
}

// Helper to extract short file basename
function getFileBasename(filePath) {
  if (!filePath) return "Unknown";
  const parts = filePath.split(/[\\/]/);
  return parts[parts.length - 1];
}

export default function AIErrorLogsDashboard() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sort, setSort] = useState("latest");
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);

  // Selected Log for Inspector Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch error logs
  const fetchErrorLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        sort,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (conversationId) params.conversation_id = conversationId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await api.get("/ai/error-logs", { params });
      if (res.data?.success) {
        setLogs(res.data.data || []);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      } else {
        setLogs([]);
        setError(res.data?.message || "Failed to load error logs.");
      }
    } catch (err) {
      console.error("Failed to fetch AI error logs:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not connect to AI error logs service."
      );
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sort, debouncedSearch, conversationId, fromDate, toDate]);

  useEffect(() => {
    fetchErrorLogs();
  }, [fetchErrorLogs]);

  // Copy helper
  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Reset filters
  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setConversationId("");
    setFromDate("");
    setToDate("");
    setSort("latest");
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    debouncedSearch || conversationId || fromDate || toDate || sort !== "latest"
  );

  return (
    <div className="space-y-5 animate-fade-up" data-testid="ai-error-logs-dashboard">
      {/* Top Header Card */}
      <div className="bg-gradient-to-r from-slate-900 via-rose-950 to-slate-900 border border-slate-800 rounded-xl p-5 text-white shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-80 bg-radial from-rose-500/10 to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0 shadow-inner">
              <Bug className="w-6 h-6 text-rose-400" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-lg font-heading font-bold text-white tracking-tight">
                  AI Chat Error Logs
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-500/20 text-rose-300 border border-rose-500/30">
                  {pagination.total} Recorded
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-1">
                Real-time tracking of AI agent runtime exceptions, Gemini API errors, and tool failures.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => fetchErrorLogs()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white/10 hover:bg-white/15 active:bg-white/20 text-white rounded-lg text-xs font-semibold transition-colors border border-white/15 cursor-pointer disabled:opacity-50"
              title="Refresh logs"
            >
              <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search Query */}
          <div className="lg:col-span-2 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search prompt, error message, or file..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Conversation ID Filter */}
          <div className="relative">
            <MessageSquare className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="number"
              placeholder="Conv ID (e.g. 6)"
              value={conversationId}
              onChange={(e) => {
                setConversationId(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          {/* Date Range Start */}
          <div className="relative">
            <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              title="From date"
              className="w-full pl-9 pr-2 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-slate-700"
            />
          </div>

          {/* Date Range End & Sort */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                title="To date"
                className="w-full pl-9 pr-2 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-slate-700"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className="px-2.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary font-medium text-slate-700"
            >
              <option value="latest">Latest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>
        </div>

        {/* Active Filter Indicators */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-2 border-t border-slate-100 flex-wrap gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <span>Active filters:</span>
              {debouncedSearch && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                  Search: &ldquo;{debouncedSearch}&rdquo;
                </span>
              )}
              {conversationId && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                  Conv #{conversationId}
                </span>
              )}
              {(fromDate || toDate) && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                  Date: {fromDate || "Any"} → {toDate || "Any"}
                </span>
              )}
              {sort === "oldest" && (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                  Sort: Oldest first
                </span>
              )}
            </div>
            <button
              onClick={resetFilters}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold cursor-pointer underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Main Error Logs Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        {loading ? (
          <div className="py-20 text-center text-slate-400">
            <div className="flex flex-col items-center justify-center gap-2">
              <RefreshCw className="w-7 h-7 animate-spin text-rose-500" />
              <span className="text-xs font-semibold text-slate-600">
                Fetching error logs...
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="font-heading font-bold text-slate-800 text-base">
              Failed to load error logs
            </div>
            <p className="text-xs text-slate-500 max-w-md mx-auto">{error}</p>
            <button
              onClick={() => fetchErrorLogs()}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
            >
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="py-20 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-200">
              <Check className="w-6 h-6" />
            </div>
            <div className="font-heading font-bold text-slate-800 text-base">
              No Error Logs Found
            </div>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              {hasActiveFilters
                ? "No error records matched the active filter criteria. Try adjusting or clearing your filters."
                : "Great news! No AI chat runtime exceptions or failures have been logged."}
            </p>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium cursor-pointer transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[11px]">
                  <th className="py-3 px-4 w-16">Log #</th>
                  <th className="py-3 px-4 w-28">Conv ID</th>
                  <th className="py-3 px-4 w-40">Timestamp</th>
                  <th className="py-3 px-4 w-60">User Prompt</th>
                  <th className="py-3 px-4">Error / Exception</th>
                  <th className="py-3 px-4 w-44">Source File & Line</th>
                  <th className="py-3 px-4 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => {
                  const parsed = formatErrorMessage(log.error_message);
                  const isCopied = copiedId === log.id;
                  const basename = getFileBasename(log.file);

                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-rose-50/30 transition-colors group"
                    >
                      {/* Log ID */}
                      <td className="py-3 px-4 font-mono font-bold text-slate-500">
                        #{log.id}
                      </td>

                      {/* Conversation ID */}
                      <td className="py-3 px-4">
                        {log.conversation_id ? (
                          <button
                            onClick={() => {
                              setConversationId(String(log.conversation_id));
                              setPage(1);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold border border-indigo-200 transition-colors cursor-pointer text-xs"
                            title={`Filter by Conversation #${log.conversation_id}`}
                          >
                            <MessageSquare className="w-3 h-3" />
                            <span>Conv #{log.conversation_id}</span>
                          </button>
                        ) : (
                          <span className="text-slate-400 italic">None</span>
                        )}
                      </td>

                      {/* Timestamp & Time ago */}
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">
                          {formatDate(log.created_at)}
                        </div>
                        {log.time_ago && (
                          <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{log.time_ago}</span>
                          </div>
                        )}
                      </td>

                      {/* User Prompt */}
                      <td className="py-3 px-4">
                        {log.user_message ? (
                          <div
                            className="p-2 rounded-lg bg-slate-100/80 border border-slate-200/80 text-slate-700 line-clamp-2 text-xs font-normal"
                            title={log.user_message}
                          >
                            &ldquo;{log.user_message}&rdquo;
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">No message captured</span>
                        )}
                      </td>

                      {/* Error Message */}
                      <td className="py-3 px-4">
                        <div className="font-mono text-[11px] text-rose-900 bg-rose-50/70 border border-rose-200/70 rounded-md p-2 max-w-md xl:max-w-xl">
                          <div className="line-clamp-2 font-semibold">
                            {parsed.prefix || log.error_message}
                          </div>
                          {parsed.isJson && (
                            <span className="mt-1 inline-block text-[10px] font-sans px-1.5 py-0.2 rounded bg-rose-200/60 text-rose-800 font-bold">
                              Includes JSON payload
                            </span>
                          )}
                        </div>
                      </td>

                      {/* File & Line */}
                      <td className="py-3 px-4">
                        <div
                          className="font-mono text-xs text-slate-700 font-semibold truncate flex items-center gap-1.5"
                          title={log.file}
                        >
                          <FileCode className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{basename}</span>
                          {log.line && (
                            <span className="text-rose-600 font-bold">
                              :{log.line}
                            </span>
                          )}
                        </div>
                        <div
                          className="text-[10px] text-slate-400 truncate mt-0.5"
                          title={log.file}
                        >
                          {log.file}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
                            title="Inspect error details"
                          >
                            <Eye className="w-4 h-4 text-primary" />
                          </button>
                          <button
                            onClick={() => handleCopy(log.error_message, log.id)}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                            title="Copy error message"
                          >
                            {isCopied ? (
                              <Check className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && logs.length > 0 && (
          <div className="py-3 px-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-xs font-semibold focus:outline-hidden"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>per page</span>
              <span className="text-slate-400 mx-1">|</span>
              <span>
                Showing <strong>{(page - 1) * perPage + 1}</strong> to{" "}
                <strong>{Math.min(page * perPage, pagination.total)}</strong> of{" "}
                <strong>{pagination.total}</strong> errors
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:pointer-events-none rounded-lg text-xs font-semibold transition-colors flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Previous</span>
              </button>

              <span className="px-2 text-xs font-semibold text-slate-700">
                Page {page} of {pagination.last_page || 1}
              </span>

              <button
                onClick={() => setPage((p) => Math.min(pagination.last_page || 1, p + 1))}
                disabled={page >= (pagination.last_page || 1)}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:pointer-events-none rounded-lg text-xs font-semibold transition-colors flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Inspector Detail Modal */}
      {selectedLog && (
        <ErrorInspectorModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

// Error Inspector Modal Component
function ErrorInspectorModal({ log, onClose }) {
  const [copied, setCopied] = useState(false);
  const parsed = formatErrorMessage(log.error_message);

  const copyAll = () => {
    const payload = `Error Log #${log.id}
Conversation ID: ${log.conversation_id || "N/A"}
Timestamp: ${log.created_at}
File: ${log.file}:${log.line}
User Prompt: ${log.user_message || "N/A"}

Exception Details:
${log.error_message}`;
    navigator.clipboard.writeText(payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-fade-up"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
              <Bug className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-bold text-base text-white">
                  Error Log Inspector
                </h3>
                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-white/10 text-rose-300 border border-white/15">
                  #{log.id}
                </span>
                {log.conversation_id && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
                    Conv #{log.conversation_id}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Logged at: {formatDate(log.created_at)} ({log.time_ago || "recently"})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-4 text-xs flex-1">
          {/* User Prompt */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
              <span>User Input Message</span>
            </div>
            <div className="text-sm font-medium text-slate-900">
              {log.user_message ? (
                <span>&ldquo;{log.user_message}&rdquo;</span>
              ) : (
                <span className="text-slate-400 italic">No input message captured</span>
              )}
            </div>
          </div>

          {/* Origin File & Line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <FileCode className="w-3.5 h-3.5 text-slate-500" />
                <span>Origin File</span>
              </div>
              <div className="font-mono text-xs font-bold text-slate-800 mt-1 break-all">
                {log.file || "N/A"}
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Code className="w-3.5 h-3.5 text-slate-500" />
                <span>Line Number</span>
              </div>
              <div className="font-mono text-xs font-bold text-rose-600 mt-1">
                Line {log.line || "N/A"}
              </div>
            </div>
          </div>

          {/* Exception Details */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Terminal className="w-3.5 h-3.5 text-rose-500" />
                <span>Exception & Error Message</span>
              </span>
              <button
                onClick={copyAll}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline cursor-pointer"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-emerald-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy Full Log</span>
                  </>
                )}
              </button>
            </div>

            <div className="bg-slate-900 text-rose-200 font-mono text-xs p-4 rounded-xl border border-slate-800 overflow-x-auto whitespace-pre-wrap leading-relaxed shadow-inner">
              {parsed.prefix && <div>{parsed.prefix}</div>}
              {parsed.json && (
                <div className="mt-2 pt-2 border-t border-slate-800 text-amber-300">
                  {parsed.json}
                </div>
              )}
              {!parsed.json && !parsed.prefix && log.error_message}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="text-[11px] text-slate-400 font-mono">
            Log ID: {log.id}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyAll}
              className="px-3.5 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold rounded-lg text-xs transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg text-xs transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
